import clsx from 'clsx';
import type { ReactNode } from 'react';

type Variant = 'error' | 'warn' | 'success' | 'info';

interface Props {
  variant?: Variant;
  children: ReactNode;
  className?: string;
}

const STYLES: Record<Variant, { box: string; text: string; icon: JSX.Element }> = {
  error: {
    box: 'bg-red-50 border-flag-block/40',
    text: 'text-flag-block',
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </>
    ),
  },
  warn: {
    box: 'bg-amber-50 border-flag-warn/40',
    text: 'text-flag-warn',
    icon: (
      <>
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
  success: {
    box: 'bg-accent-soft border-accent/40',
    text: 'text-evt-rx-ink',
    icon: (
      <>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </>
    ),
  },
  info: {
    box: 'bg-canvas border-border',
    text: 'text-ink',
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </>
    ),
  },
};

export function InlineAlert({ variant = 'error', children, className }: Props) {
  const s = STYLES[variant];
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={clsx(
        'rounded-card border px-3 py-2.5 flex items-start gap-2',
        s.box,
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={clsx('h-4 w-4 mt-0.5 shrink-0', s.text)}
        aria-hidden="true"
      >
        {s.icon}
      </svg>
      <p className={clsx('text-sm leading-snug', s.text)}>{children}</p>
    </div>
  );
}
